import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { 
  AlertCircle, 
  User, 
  FileText, 
  Building, 
  ChevronRight,
  X,
  Phone,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ProfileCompletionWizard from './ProfileCompletionWizard';
import PhoneVerificationModal from './PhoneVerificationModal';
import axios from 'axios';

const token = 'cookie-auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ProfileCompletionBanner - Bannière rappelant de compléter le profil
 * Affichée pour tous les utilisateurs si des informations manquent
 * 
 * Inclut:
 * - Bouton "Compléter mon profil"
 * - Bouton "Vérifier mon numéro" (si téléphone non vérifié)
 * - Chip "Téléphone non vérifié" dans les items à compléter
 */
const ProfileCompletionBanner = () => {
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneVerificationStatus, setPhoneVerificationStatus] = useState(null);

  useEffect(() => {
    if (user) {
      fetchPhoneVerificationStatus();
    }
  }, [user]);

  useEffect(() => {
    if (user && phoneVerificationStatus !== null) {
      const data = calculateProfileCompletion(user, phoneVerificationStatus);
      setCompletionData(data);
      
      // Check if user has dismissed this session
      const dismissedKey = `profile_banner_dismissed_${user.id}`;
      const wasDismissed = sessionStorage.getItem(dismissedKey);
      if (wasDismissed) {
        setDismissed(true);
      }
    }
  }, [user, phoneVerificationStatus]);

  const fetchPhoneVerificationStatus = async () => {
    try {

      if (!token) {
        setPhoneVerificationStatus({ is_verified: false });
        return;
      }

      const response = await axios.get(`${API}/phone-verification/status`, {});
      
      setPhoneVerificationStatus(response.data);
    } catch (error) {
      console.error('Error fetching phone verification status:', error);
      // Fallback: check user object
      setPhoneVerificationStatus({ 
        is_verified: user?.phoneVerified || user?.notificationPhoneVerified || false 
      });
    }
  };

  const calculateProfileCompletion = (userData, phoneStatus) => {
    const role = userData?.role || '';
    const missingFields = [];
    let totalFields = 0;
    let completedFields = 0;

    // Champs communs à tous les utilisateurs
    const commonFields = [
      { key: 'firstName', label: 'Prénom' },
      { key: 'lastName', label: 'Nom' },
      { key: 'phone', label: 'Téléphone' },
      { key: 'email', label: 'Email' }
    ];

    commonFields.forEach(field => {
      totalFields++;
      if (userData[field.key]) {
        completedFields++;
      } else {
        missingFields.push({ ...field, category: 'personal' });
      }
    });

    // Vérification du téléphone (critère de complétude)
    const hasPhone = userData.phone || userData.telephone;
    if (hasPhone) {
      totalFields++;
      if (phoneStatus?.is_verified) {
        completedFields++;
      } else {
        missingFields.push({ 
          key: 'phoneVerified', 
          label: 'Téléphone non vérifié', 
          category: 'verification' 
        });
      }
    }

    // Champs spécifiques selon le rôle
    if (role === 'chauffeur' || role === 'driver') {
      const driverDocFields = [
        { key: 'cniNumber', label: 'Numéro CNI' },
        { key: 'permitType', label: 'Type de permis' },
        { key: 'permitNumber', label: 'Numéro de permis' },
        { key: 'permitExpirationDate', label: 'Date expiration permis' }
      ];

      driverDocFields.forEach(field => {
        totalFields++;
        if (userData[field.key]) {
          completedFields++;
        } else {
          missingFields.push({ ...field, category: 'documents' });
        }
      });

      if (userData.accountType === 'company') {
        const companyFields = [
          { key: 'companyName', label: 'Nom de la société' }
        ];

        companyFields.forEach(field => {
          totalFields++;
          if (userData[field.key]) {
            completedFields++;
          } else {
            missingFields.push({ ...field, category: 'company' });
          }
        });
      }
    } else if (role === 'client' || role === 'expediteur') {
      const clientFields = [
        { key: 'companyName', label: 'Nom entreprise' },
        { key: 'address', label: 'Adresse' }
      ];

      clientFields.forEach(field => {
        totalFields++;
        if (userData[field.key]) {
          completedFields++;
        } else {
          missingFields.push({ ...field, category: 'company' });
        }
      });

      // RCCM / DFE (NCC) : facultatifs — collectés au moment de la facturation FNE,
      // jamais exigés ni comptés dans la complétude du profil.
    }

    const percentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 100;

    return {
      percentage,
      missingFields,
      totalFields,
      completedFields,
      isComplete: percentage === 100,
      phoneNeedsVerification: hasPhone && !phoneStatus?.is_verified
    };
  };

  const handleDismiss = () => {
    if (user?.id) {
      sessionStorage.setItem(`profile_banner_dismissed_${user.id}`, 'true');
    }
    setDismissed(true);
  };

  const handleCompleteProfile = () => {
    setShowWizard(true);
  };

  const handleVerifyPhone = () => {
    setShowPhoneVerification(true);
  };

  const handlePhoneVerified = async () => {
    // Rafraîchir le statut de vérification
    await fetchPhoneVerificationStatus();
    
    // Rafraîchir les données utilisateur
    if (refreshUser) {
      const updatedUser = await refreshUser();
      if (updatedUser && phoneVerificationStatus) {
        const data = calculateProfileCompletion(updatedUser, { is_verified: true });
        setCompletionData(data);
      }
    }
  };

  const handleWizardComplete = async () => {
    if (refreshUser) {
      const updatedUser = await refreshUser();
      await fetchPhoneVerificationStatus();
      
      if (updatedUser) {
        const data = calculateProfileCompletion(updatedUser, phoneVerificationStatus);
        setCompletionData(data);
        if (data.isComplete) {
          setDismissed(true);
        }
      }
    }
  };

  // Ne pas afficher si pas d'utilisateur, si profil complet, ou si bannière ignorée
  if (!user || !completionData || completionData.isComplete || dismissed) {
    return null;
  }

  // Ne pas afficher pour le staff interne (ils n'ont pas de profil client à compléter)
  // RBAC V2 — couvre admin/lead_ops/ops/operateur/lead_commercial/commercial/lead_field_ops/field_ops/finance/auditeur
  const isStaff = (
    user.role === 'admin' || user.role === 'super_admin' ||
    user.role === 'lead_ops' || user.role === 'ops' || user.role === 'operateur' ||
    user.role === 'lead_field_ops' || user.role === 'field_ops' ||
    user.role === 'lead_commercial' || user.role === 'commercial' ||
    user.role === 'finance' || user.role === 'auditeur'
  );
  if (isStaff) {
    return null;
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'personal':
        return <User className="w-4 h-4" />;
      case 'documents':
        return <FileText className="w-4 h-4" />;
      case 'company':
      case 'legal':
        return <Building className="w-4 h-4" />;
      case 'verification':
        return <ShieldCheck className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return 'bg-red-500';
    if (percentage < 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  // Grouper les champs manquants par catégorie
  const groupedMissing = completionData.missingFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {});

  // Déterminer si le bouton "Vérifier mon numéro" doit être affiché
  const showVerifyButton = completionData.phoneNeedsVerification;
  const userPhone = user.phone || user.telephone;

  return (
    <Alert className="mb-6 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 relative">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-amber-100 transition-colors"
        aria-label="Fermer"
      >
        <X className="w-4 h-4 text-amber-600" />
      </button>
      
      <div className="flex items-start gap-4 pr-8">
        <div className="flex-shrink-0 mt-1">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-amber-900">
              Complétez votre profil
            </h3>
            <span className="text-sm font-medium text-amber-700">
              {completionData.percentage}% complet
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="w-full h-2 bg-amber-100 rounded-full mb-3 overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${getProgressColor(completionData.percentage)}`}
              style={{ width: `${completionData.percentage}%` }}
            />
          </div>
          
          <AlertDescription className="text-amber-800 mb-3">
            <p className="mb-2">
              Pour profiter pleinement de nos services, veuillez compléter les informations suivantes :
            </p>
            
            {/* Liste des champs manquants par catégorie */}
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(groupedMissing).map(([category, fields]) => (
                <div 
                  key={category}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs ${
                    category === 'verification' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {getCategoryIcon(category)}
                  <span>
                    {fields.map(f => f.label).slice(0, 2).join(', ')}
                    {fields.length > 2 && ` +${fields.length - 2}`}
                  </span>
                </div>
              ))}
            </div>
          </AlertDescription>
          
          {/* Boutons d'action */}
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleCompleteProfile}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Compléter mon profil
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            
            {/* Bouton Vérifier mon numéro - désactivé (pas d'intégration Twilio) */}
            {false && showVerifyButton && (
              <Button 
                onClick={handleVerifyPhone}
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <Phone className="w-4 h-4 mr-1" />
                Vérifier mon numéro
              </Button>
            )}
            
            {/* Indicateur numéro vérifié */}
            {!showVerifyButton && userPhone && phoneVerificationStatus?.is_verified && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-md text-sm">
                <CheckCircle2 className="w-4 h-4" />
                Numéro vérifié
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Profile Completion Wizard */}
      <ProfileCompletionWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onComplete={handleWizardComplete}
      />
      
      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        open={showPhoneVerification}
        onClose={() => setShowPhoneVerification(false)}
        onVerified={handlePhoneVerified}
        phone={userPhone}
        allowPhoneEdit={false}
      />
    </Alert>
  );
};

export default ProfileCompletionBanner;
